import type { Industry } from '../types/account.js';
import { portalPersonFromUser, type ManagerUser } from './portalReport/reportContacts.js';

// CLIENT PORTAL §5/§5a — the Kyck people a client can contact, derived from the
// portal's own data.
//
// ⚠️⚠️ "FRANCHISE" APPEARS NOWHERE IN THIS FILE, AND THAT IS THE POINT. Scott
// asked for "the rest of the franchise account manage team", and the obvious
// implementation — filter accounts where `industry` contains "franchise" — is
// the bug the request was written to avoid: TWO of the four industries carry the
// word (`fitness_franchise`, `automotive_franchise`), while Kyck's own team roles
// separate Head of Franchise from Head of Automotive. A literal name match would
// put automotive account managers on a Snap portal.
//
// ⚠️ SO THE INDUSTRY COMES FROM THE VIEWING CLIENT'S OWN SCOPE. A fitness client
// sees the fitness account managers; an automotive client sees the automotive
// ones. Same mechanism, no second rule when the first Kyck Automotive portal
// arrives. A literal industry string in this feature is a defect, and
// `portalTeam.test.ts` asserts on this file's SOURCE that none appears.
//
// ⚠️⚠️ THE LIST IS ASSIGNMENT-DERIVED, NOT ROLE-DERIVED, and the difference is
// the whole safety argument. A query like "staff whose role is account manager"
// would publish the next person given that role to every client in the industry,
// with nobody deciding to. Deriving from `accountManagerId` means a person
// appears only once somebody has actually attached them to an account — and that
// attachment IS the deliberate human decision.
//
// ⚠️ CONSEQUENTLY IT CAN ONLY EVER PRODUCE ACCOUNT MANAGERS. `accountManagerId`
// is the only staff link on an Account; there is no strategist, designer or
// other staff field anywhere on the model. The strategists and designers on
// kyck's own website are absent because they are ATTACHED TO NOTHING THIS PORTAL
// CAN READ — not because they were excluded on purpose. Fixing that at the root
// (staff fields on `Account`, plus a backfill) is its own round.
//
// ⚠️⚠️ WHICH IS WHY `TEAM_ADDITIONS` IS A GENERAL LIST RATHER THAN A HARDCODED
// SCOTT. He is Agency Director and manages no accounts, so he can only be added
// by hand; building that as a config makes adding a strategist later a config
// edit rather than a code change.

/**
 * One Kyck person, as the client's home page renders them.
 *
 * ⚠️ `phone` IS `users.phoneNumber`, PUBLISHED AS-IS — Scott's deliberate
 * acceptance, recorded rather than assumed. Stated plainly so it is not
 * rediscovered as a surprise: editing a staff member's phone number in the CRM
 * changes what every portal client in their industry sees, and the consequence
 * is invisible both at that edit and at the moment of assignment. The staff user
 * form carries one line of helper text saying so, which is the only place the
 * decision is actually made.
 *
 * ⚠️ NULL SHOWS NO PHONE. Never a fallback to another number, never the office
 * line dressed as a direct one.
 */
export interface PortalTeamMember {
  name: string;
  email: string;
  phone: string | null;
  /** A file under `/team/`, or null. ⚠️ Never derived — see `TEAM_PHOTOS`. */
  photoUrl: string | null;
  /**
   * What they do, as the client reads it (§3b, 2026-09-04).
   *
   * ⚠️⚠️ NOT `UserProfile.jobTitle`, WHICH ALREADY EXISTS AND IS SELF-SERVICE.
   * Each person edits their own in Settings → Profile, so reading it would put
   * whatever an individual typed onto a client-facing page — and Scott specified
   * exact wording. A default plus two overrides is what he described, and it does
   * not depend on eighteen people maintaining their own profile text.
   */
  title: string;
}

/** What `GET /api/portal/team` returns. */
export interface PortalHomeTeam {
  /**
   * The client's OWN account manager, or null when the scope's accounts do not
   * share one. Rendered large, with contact details; excluded from `team` below,
   * because one person in two places on one page reads as a duplicate rather
   * than as emphasis.
   */
  accountManager: PortalTeamMember | null;
  /** Everyone else, smaller. Ordered; deduplicated; may be empty. */
  team: PortalTeamMember[];
  /**
   * The industries the list was derived from (§3a).
   *
   * ⚠️⚠️ THE HEADING IS DERIVED FROM THIS, NOT SENT AS COPY, and that is what makes
   * it impossible for the heading to name a different industry from the people
   * beneath it: the browser gets the EXACT array the server filtered by, and
   * `teamHeading` is a function of it. Sending a finished string would be a second
   * statement of the same fact with its own chance to be wrong.
   */
  industries: Industry[];
}

/**
 * Staff who appear on the list without managing an account.
 *
 * ⚠️ KEYED BY EMAIL because a uid is a Firestore auto-id that cannot be
 * committed from outside the live database — the same constraint
 * `BRAND_ACCOUNT_THEMES` documents. An address that matches no staff user
 * resolves to NOTHING rather than to a placeholder: an addition we cannot
 * resolve is a person we cannot put a real phone number against.
 *
 * ⚠️⚠️ EACH CARRIES ITS OWN POSITION (§3c, 2026-09-04). Scott asked to be FIRST,
 * ahead of the derived account managers — he is the Agency Director and manages no
 * accounts, so his place in the list is an ordering CONSTANT rather than anything
 * a sort could produce.
 *
 * ⚠️ EXPRESSED AS A FIELD RATHER THAN HARDCODED, because round 9 made this a
 * GENERAL list on purpose: adding a strategist later must stay a config edit. A
 * special case for one person would have thrown that away to satisfy one ask.
 * `first` renders ahead of the managers, `last` after them, each group keeping
 * the order written here.
 */
export const TEAM_ADDITIONS: readonly { email: string; position: 'first' | 'last' }[] = [
  { email: 'scott@kyck.com.au', position: 'first' },
];

/**
 * Job titles, BY NAME, with a default.
 *
 * ⚠️⚠️ A DEFAULT PLUS OVERRIDES, NOT `UserProfile.jobTitle`. That field exists and
 * is SELF-SERVICE — each person edits their own in Settings → Profile — so reading it
 * would publish whatever an individual typed. Scott specified exact wording, and
 * this is two entries rather than a dependency on eighteen people.
 *
 * ⚠️⚠️ A DELIBERATE DIVERGENCE FROM THE WEBSITE, RECORDED SO NOBODY "CORRECTS" IT:
 * kyck.com.au calls Ethan Bailey *National Account Director*; the portal says
 * *NZ Account Manager*, which is what Scott asked for. Two audiences, two
 * vocabularies.
 *
 * ⚠️ Keyed the same way as `TEAM_PHOTOS`, and matched EXACTLY, for the same
 * reason: a fuzzy match is a derivation. An unlisted name gets the DEFAULT, which
 * is the true statement about almost everyone on this list.
 */
export const TEAM_TITLE_DEFAULT = 'Account Manager';
export const TEAM_TITLES: Readonly<Record<string, string>> = {
  'Scott Mackenroth': 'Agency Director',
  'Ethan Bailey': 'NZ Account Manager',
};

/** The title for a staff name. ⚠️ Never empty — everyone on this list has a role. */
export function teamTitle(name: string): string {
  return TEAM_TITLES[name.trim()] ?? TEAM_TITLE_DEFAULT;
}

/**
 * Headshots, BY NAME, and explicit.
 *
 * ⚠️⚠️ NEVER A SLUG DERIVED FROM THE NAME. "Courtney Stevens" →
 * `courtney-stevens.webp` works until somebody is called O'Brien, changes their
 * name, or joins without a photo — and then it 404s into a broken image on a
 * client's home page. No entry means NO IMAGE, which degrades to a monogram.
 *
 * ⚠️ A NAME CHANGE DROPS THE PHOTO rather than showing the wrong one, which is
 * the safe direction. Keying by email would be more stable, but we hold no
 * verified address for most of these people and inventing one from a name is the
 * same derivation this map exists to refuse.
 *
 * ⚠️ THE KEY IS THE STAFF USER'S `displayName`, so an entry only fires when the
 * CRM name matches exactly. That is deliberate: a fuzzy match is a derivation.
 *
 * The files are kyck's own website headshots, already WebP and already
 * right-sized (600px square, 8–39KB), copied rather than re-encoded.
 */
export const TEAM_PHOTOS: Readonly<Record<string, string>> = {
  'Scott Mackenroth': 'scott-mackenroth.webp',
  'Ethan Bailey': 'ethan-bailey.webp',
  'Ryley Sheldon': 'ryley-sheldon.webp',
  'Jade Cross': 'jade-cross.webp',
  'Ben Curcio': 'ben-curcio.webp',
  'Thiago Pires': 'thiago-pires.webp',
  'Nathan McMillan': 'nathan-mcmillan.webp',
  'Sandrea Siow': 'sandrea-siow.webp',
  'Stacey Ellis': 'stacey-ellis.webp',
  'Sammy Wilson': 'sammy-wilson.webp',
  'Courtney Stevens': 'courtney-stevens.webp',
  'Joe Bruce': 'joe-bruce.webp',
  'Lina Beckett': 'lina-beckett.webp',
  'Elliot Plunkett': 'elliot-plunkett.webp',
  'Jessica Pollard': 'jessica-pollard.webp',
  'Sheridan Ferguson': 'sheridan-ferguson.webp',
  'Rachel McLean': 'rachel-mclean.webp',
  'Paula Ferreira': 'paula-ferreira.webp',
};

/** The photo for a staff name, or null. ⚠️ Absence is a real answer. */
export function teamPhotoUrl(name: string | null): string | null {
  if (!name) return null;
  const file = TEAM_PHOTOS[name.trim()];
  return file ? `/team/${file}` : null;
}

/**
 * The industries this client's scope covers, in a stable order.
 *
 * ⚠️⚠️ A MIXED SCOPE RESOLVES TO THE UNION, NOT TO THE FIRST ACCOUNT'S INDUSTRY.
 * A group can hold accounts in two industries, and silently taking the first
 * would show a client half their team with nothing saying so. Sorted so the
 * answer does not depend on roster order.
 *
 * ⚠️ An account with NO industry contributes nothing rather than a fourth
 * bucket — `other` is a real industry value and must not be confused with an
 * unset one.
 */
export function scopeIndustries(
  accounts: ReadonlyArray<{ industry?: Industry | null }>,
): Industry[] {
  const seen = new Set<Industry>();
  for (const a of accounts) if (a.industry) seen.add(a.industry);
  return [...seen].sort();
}

// ⚠️⚠️ THE HEADING LIVES IN `portalTeamHeading.ts`, NOT HERE (§3a). Mapping an
// industry to words must NAME the industries, and this file's source guard
// forbids exactly that — it is round 9 §5's guarantee that the team is DERIVED
// from the viewer's industry rather than filtered by a literal. Splitting the
// copy out keeps that rule absolute instead of carving an exclusion into it,
// which is how a guard stops meaning anything.
/**
 * The uids to show, in order: account managers for the viewer's industries,
 * then the explicit additions.
 *
 * ⚠️ THE CLIENT'S OWN ACCOUNT MANAGER IS EXCLUDED — they are the main block, and
 * appearing twice on one page reads as a duplicate rather than as emphasis.
 *
 * ⚠️ DEDUPLICATED. One person manages several accounts in an industry and must
 * appear once; an addition who is ALSO a manager keeps their manager position
 * rather than being listed twice.
 *
 * ⚠️ Account managers are ordered by NAME, not by roster order, so the list is
 * stable between requests. Additions keep `TEAM_ADDITIONS`' own order, which is
 * meaningful.
 *
 * ⚠️⚠️ AND AN ADDITION MAY SIT BEFORE THEM (§3c). Scott is first, ahead of the
 * derived managers — an ordering constant, not something a sort produces, since
 * he manages no accounts at all.
 */
export function orderTeamUids(input: {
  /** The whole roster, already narrowed to whatever the caller considers live. */
  roster: ReadonlyArray<{ industry?: Industry | null; accountManagerId?: string | null }>;
  industries: readonly Industry[];
  /** uid → display name, for the manager ordering. */
  nameByUid: ReadonlyMap<string, string>;
  /** The client's own account manager, excluded. */
  excludeUid: string | null;
  /**
   * Resolved from `TEAM_ADDITIONS`, in that order, each carrying its position.
   * Unresolvable ones are absent.
   */
  additionUids: readonly { uid: string; position: 'first' | 'last' }[];
}): string[] {
  const wanted = new Set(input.industries);
  const managers = new Set<string>();
  for (const a of input.roster) {
    if (!a.industry || !wanted.has(a.industry)) continue;
    const uid = a.accountManagerId;
    if (uid && uid !== input.excludeUid) managers.add(uid);
  }
  const ordered = [...managers].sort((a, b) =>
    (input.nameByUid.get(a) ?? a).localeCompare(input.nameByUid.get(b) ?? b),
  );
  // ⚠️ THE FIRST-ADDITIONS ARE PLACED BEFORE THE DEDUPE RUNS OVER THE MANAGERS,
  // so an addition who is ALSO a manager keeps the position their entry asks for
  // rather than falling back to the alphabetical run. Round 9's rule was the
  // opposite (managers won), and it was only right while every addition sat last.
  const take = (position: 'first' | 'last') =>
    input.additionUids.filter((a) => a.position === position).map((a) => a.uid);

  const out: string[] = [];
  const have = new Set<string>();
  const push = (uid: string) => {
    if (uid === input.excludeUid || have.has(uid)) return;
    have.add(uid);
    out.push(uid);
  };

  take('first').forEach(push);
  ordered.forEach(push);
  take('last').forEach(push);
  return out;
}

/**
 * A staff user as the client sees them, or null when we cannot name them.
 *
 * ⚠️⚠️ THE NAME, EMAIL AND PHONE COME FROM `portalPersonFromUser` — THE SAME
 * FUNCTION THE REPORT'S "Account manager" LINE USES. Two implementations of
 * "how is a Kyck person shown to a client" is how the report comes to name
 * somebody one way and the home page another, and a client would read that as
 * the two pages disagreeing about who their account manager is. This adds only
 * the photo.
 *
 * ⚠️ NO NAME MEANS NO ENTRY (that rule lives there): a contact block headed by an
 * email address is worse than one fewer person on the list, and `displayName` is
 * absent on legacy and freshly-bootstrapped user docs.
 */
// ⚠️ THE SAME `ManagerUser` THE REPORT'S RESOLVER TAKES, not a structurally
// similar copy. `portalPersonFromUser` exists so the two pages cannot disagree
// about a person; a second parameter type here would let one side accept a
// source the other rejects, which is the drift the shared helper was extracted
// to prevent — and is how the home page came to read a projection with no
// phone number on it while the report read whole documents.
export function toTeamMember(user: ManagerUser): PortalTeamMember | null {
  const person = portalPersonFromUser(user);
  if (!person) return null;
  return { ...person, photoUrl: teamPhotoUrl(person.name), title: teamTitle(person.name) };
}

/**
 * Initials, for the monogram a missing photo degrades to.
 *
 * ⚠️ A MONOGRAM IS AN HONEST PLACEHOLDER; a substituted stock face is not. At
 * most two letters, from the first and last words, so a middle name does not
 * produce a three-letter block that reads as an acronym.
 */
export function teamInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0] as string;
  const last = parts[parts.length - 1] as string;
  const letters = parts.length === 1 ? first.slice(0, 1) : `${first.slice(0, 1)}${last.slice(0, 1)}`;
  return letters.toUpperCase();
}
